import { ADMIN_PAGE_NAMES } from '../util/adminPages';

/**
 * Konvers AI chat widget.
 *
 * Loaded on every page except the ones listed below. A blocklist rather than
 * an allowlist on purpose: support should be reachable almost everywhere, and
 * naming the few focused-task screens where a chat bubble would be in the way
 * is easier to keep correct than enumerating every browse route.
 *
 * Routes are matched by NAME, not by path. Names are the stable identifier in
 * routeConfiguration.js — paths get reshaped (/l/:slug/:id/:variant and
 * friends) without the page's identity changing.
 */

export const CHAT_WIDGET_SRC = 'https://app.prod.konversai.no/static/widget/bubble.js';

// Provided by Konvers. The same key on staging and production, per the client.
export const CHAT_WIDGET_TENANT_KEY = 'b265dcae-09a6-4e0f-a781-b01c4d022d88';
export const CHAT_WIDGET_API_BASE = 'https://app.prod.konversai.no';

/**
 * Where the bubble must NOT appear. Every one of these is a screen where the
 * visitor is completing a single task and a chat bubble sits over the thing
 * they are trying to press.
 */
export const CHAT_WIDGET_BLOCKED_ROUTES = [
  // Paying. The bubble overlaps the pay button on a phone, and this is the
  // last place to introduce a distraction.
  'CheckoutPage',

  // Creating or editing a listing, including the Stripe onboarding step the
  // wizard redirects into.
  'NewListingPage',
  'EditListingPage',
  'EditListingStripeOnboardingPage',

  // Payout setup, reached from account settings. Same reasoning as above:
  // a redirect flow through Stripe that should not be interrupted.
  'StripePayoutPage',
  'StripePayoutOnboardingPage',

  // Admin tooling. Not a customer surface — every admin page, from the one
  // shared list, so a new one is covered the day it is added.
  ...ADMIN_PAGE_NAMES,

  // Signing in or up, including the SSO confirm step and password recovery.
  'LoginPage',
  'SignupPage',
  'SignupCompletePage',
  'SignupForUserTypePage',
  'ConfirmPage',
  'PasswordRecoveryPage',
  'PasswordResetPage',
];

/**
 * Should the widget show on this route?
 *
 * Unknown or unmatched routes get the widget, which is the point of a
 * blocklist: a route added later is visible to support by default, and only
 * has to be named here if it should not be.
 *
 * @param {string} routeName - `name` from the matched route
 * @returns {boolean}
 */
export const showChatWidgetOnRoute = routeName =>
  !!routeName && !CHAT_WIDGET_BLOCKED_ROUTES.includes(routeName);
